// @ts-strict-ignore
import React, { useState, useRef, useEffect } from 'react'
import { IDetectedBarcode, Scanner } from '@yudiel/react-qr-scanner'
import strings from 'mobile_web/strings'
import { ionHistory } from 'mobile_web/modules/navigation'
import { BASE_PATH, SCANNER_PAGE_SLUG } from 'mobile_web/modules/navigation/routes'
import { matchRedemptionUrl, getCodeRedeemUrl, ParsedRedemption } from 'mobile_web/modules/lockapp/codeRedemption'
import { DEFAULT_APP_INFO } from 'mobile_web/modules/settings/functions'
import { validateUrl } from 'mobile_web/modules/platform'
import { Button, Loading } from '@mytours/components'
import Modal from '../modal'
import { ScrollContent } from 'mobile_web/modules/common/components/content'
import MessageToast from '../MessageToast'
import './scanEntry.less'

interface Props {
	isOpen?: boolean
	toggleModal?: (itemId?: number) => void
	successCallback?: () => void
	errorCallback?: (error: any) => void
	// When provided, a scanned redemption QR is handed back parsed instead of navigating to the
	// redeem route — lets a caller (e.g. the bundle dialog) redeem in place. Non-redemption scans
	// are unaffected.
	onRedemption?: (parsed: ParsedRedemption) => void
	// Optional caption shown over the live camera (e.g. "Scan this bundle's QR code"). Omitted on the
	// generic scanner route, which scans any QR and has no single instruction to give.
	instruction?: string
}

interface ContainerDemension {
	width: number
	height: number
}

const ScanEntry: React.FC<Props> = ({
	isOpen,
	toggleModal,
	successCallback,
	errorCallback,
	onRedemption,
	instruction,
}): JSX.Element => {
	const [result, setResult] = useState<number>(0)
	const [error, setError] = useState<string>('')
	const [toastError, setToastError] = useState<string>('')
	const [loading, setLoading] = useState<boolean>(false)
	const [containerDemension, setContainerDemension] = useState<ContainerDemension>({
		width: null,
		height: null,
	})
	const [, setPathname] = useState<string>('')
	const [showScanner, setShowScaner] = useState<boolean>(true)

	const LOADING_DURATION = 1000
	const TOAST_DURATION = 3000

	const containerRef = useRef<HTMLDivElement>()

	const onResize = () => {
		if (containerRef && containerRef.current) {
			const { clientWidth, clientHeight } = containerRef.current
			setContainerDemension({ width: clientWidth, height: clientHeight })
		}
	}

	useEffect(
		() => () => {
			setShowScaner(false)
		},
		[],
	)

	useEffect(() => {
		setTimeout(() => onResize())
		addEventListener('resize', onResize)

		setPathname(ionHistory.location.pathname)
		const historyListener = ionHistory.listen(({ pathname: historyPathname }) => {
			// Only the inline scanner-route usage pauses the camera by route. In modal mode (e.g. the
			// bundle dialog) the parent controls mount/unmount, and the route stays on the host screen —
			// gating here would tear down the camera mid-play (AbortError) the moment the modal opens.
			if (!toggleModal) {
				setShowScaner(historyPathname.includes(`/${SCANNER_PAGE_SLUG}`))
			}
			setPathname(historyPathname)
		})

		return () => {
			removeEventListener('resize', onResize)
			historyListener()
		}
		// Mount-once: subscribe the listener a single time. toggleModal is fixed per usage (the
		// dialog always passes it, the scanner route never does), so it needn't re-subscribe.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [containerRef])

	const handleError = (error: any) => {
		try {
			if (error?.toString()) {
				error = error.toString()
				if (error.includes('NotFoundError')) {
					error = strings.camera_does_not_support
				} else if (error.includes('NotAllowedError')) {
					error = strings.do_not_have_camera_permissions
				} else if (error.includes('NoVideoInputDevicesError')) {
					error = strings.camera_does_not_support
				}
			}
		} catch (e) {
			error = strings.error_occurred
		}

		setError(error)
	}

	const qrCodeInvalid = () => {
		setTimeout(() => {
			setToastError(strings.not_valid_qr_code)
			setLoading(false)
			errorCallback && errorCallback(strings.not_valid_qr_code)
			setTimeout(() => {
				setToastError('')
			}, TOAST_DURATION)
		}, LOADING_DURATION)
	}

	const qrCodeValid = (url: URL) => {
		setTimeout(() => {
			successCallback && successCallback()
			setLoading(false)

			ionHistory.replace(url.pathname + url.search + '#qr-code')

			if (toggleModal) {
				let itemId = ''
				try {
					itemId = url.pathname.split('/').reverse()[0]
				} catch (error) {
					console.error(error)
				}
				closeModal(Number(itemId))
			}
		}, LOADING_DURATION)
	}

	const resolveUrl = async (url: URL) => {
		const iframe = document.createElement('iframe')
		const urlPromise = new Promise<string>(res => {
			iframe.onload = () => {
				try {
					res(iframe.contentWindow.location.href)
				} catch (error) {
					console.warn('resolveUrl', error)
					res(undefined)
				}
			}
		})

		iframe.src = url.href
		document.body.appendChild(iframe)
		let resolvedUrl = undefined
		try {
			resolvedUrl = await urlPromise
			return resolvedUrl && new URL(resolvedUrl)
		} catch (error) {
			console.error('resolveUrl', error)
		} finally {
			document.body.removeChild(iframe)
		}
	}

	const handleScan = async (barcodes: IDetectedBarcode[]) => {
		// use it to handle state refreshing
		setResult(result + 1)

		if (loading) {
			return
		}

		const data = barcodes[0]?.rawValue

		if (data) {
			setLoading(true)

			const appDomain = window.location.host

			let url = validateUrl(data)

			// Redemption QRs (*.stqry.link, or an in-app link) are recognised by their
			// /code/redeem/ path and accepted from the deep linking host without resolving — they need
			// not redirect to the app origin. Navigate to the local redeem route (CodeRedeem then
			// handles collection vs bundle)
			const redemption = url ? matchRedemptionUrl(url) : undefined
			if (redemption) {
				setLoading(false)
				successCallback && successCallback()
				// onRedemption path: the caller dismisses the scanner. Navigate path: the route change
				// unmounts it. So no closeModal() needed here.
				if (onRedemption) {
					onRedemption(redemption)
				} else {
					ionHistory.replace(getCodeRedeemUrl(redemption) + '#qr-code')
				}
				return
			}

			// Redeem-in-place mode (the bundle dialog passes onRedemption) only accepts redemption QRs —
			// reject anything else rather than navigating away from the host dialog to the scanned link.
			if (onRedemption) {
				qrCodeInvalid()
				return
			}

			if (url && appDomain !== url.host) {
				url = await resolveUrl(url)
			}

			if (url && appDomain === url.host) {
				// check if /mw/ path exist for the current app
				if (BASE_PATH) {
					// get app id from scanned url
					// 0 - empty, 1 - 'mw', 2 - app id
					const appId = url.pathname.split('/')[2]

					// compare scanned app id with the current app id
					if (Number(appId) === Number(DEFAULT_APP_INFO.projectId)) {
						qrCodeValid(url)
					} else {
						qrCodeInvalid()
					}

					return
				}

				// When no base path (the app has subdomain or separate domain) then qr code is valid
				qrCodeValid(url)
				return
			}

			qrCodeInvalid()
		}
	}

	const renderSquareStyles = () => {
		let styleHTML = `
			.scanner-content .scanner-container > section > * > div {
				display: none;
			}
		`
		let corners
		let background

		if (containerDemension) {
			const { width, height } = containerDemension

			const minValue = Math.min(width, height)
			const maxSquareSize = 300
			const size = minValue - 100 > maxSquareSize ? maxSquareSize : minValue - 100
			const cornerSize = Math.round(size * 0.18)
			const cornerHalfDepth = Math.round(size / 100)

			const horizontalPadding = Math.round((width - size) / 2)
			const verticalPadding = Math.round((height - size) / 2)

			corners = (
				<>
					<div
						className="border-fragment top-left"
						style={{
							top: verticalPadding - cornerHalfDepth,
							left: horizontalPadding - cornerHalfDepth,
							width: cornerSize,
							height: cornerSize,
							backgroundSize: cornerSize,
						}}
					/>
					<div
						className="border-fragment top-right"
						style={{
							top: verticalPadding - cornerHalfDepth,
							left: horizontalPadding + size + cornerHalfDepth - cornerSize,
							width: cornerSize,
							height: cornerSize,
							backgroundSize: cornerSize,
						}}
					/>
					<div
						className="border-fragment bottom-left"
						style={{
							top: verticalPadding + size + cornerHalfDepth - cornerSize,
							left: horizontalPadding - cornerHalfDepth,
							width: cornerSize,
							height: cornerSize,
							backgroundSize: cornerSize,
						}}
					/>
					<div
						className="border-fragment bottom-right"
						style={{
							top: verticalPadding + size + cornerHalfDepth - cornerSize,
							left: horizontalPadding + size + cornerHalfDepth - cornerSize,
							width: cornerSize,
							height: cornerSize,
							backgroundSize: cornerSize,
						}}
					/>
				</>
			)

			styleHTML = `
				.scanner-container > section > * > div {
					border-top-width: ${verticalPadding}px !important;
					border-bottom-width: ${verticalPadding}px !important;
					border-left-width: ${horizontalPadding}px !important;
					border-right-width: ${horizontalPadding}px !important;
				}
			`

			background = (
				<div
					style={{
						width: `${size}px`,
						height: `${size}px`,
						borderTop: `${(height - size) / 2}px solid rgba(0, 0, 0, 0.3)`,
						borderBottom: `${(height - size) / 2}px solid rgba(0, 0, 0, 0.3)`,
						borderLeft: `${(width - size) / 2}px solid rgba(0, 0, 0, 0.3)`,
						borderRight: `${(width - size) / 2}px solid rgba(0, 0, 0, 0.3)`,
						boxSizing: 'content-box',
					}}
				/>
			)
		}

		return (
			<>
				<style dangerouslySetInnerHTML={{ __html: styleHTML }} />
				{corners}
				<div
					style={{
						position: 'absolute',
						top: 0,
						width: '100%',
						height: '100%',
						zIndex: 1,
					}}
				>
					{background}
				</div>
			</>
		)
	}

	const closeModal = (itemId?: number) => {
		if (isOpen) {
			toggleModal(itemId)
		}
	}

	const renderContent = () => (
		<div ref={containerRef} className="scanner-container">
			{error ? (
				<div className="error-message">{error}</div>
			) : (
				<>
					{renderSquareStyles()}
					{showScanner && (
						<Scanner
							formats={['qr_code']}
							scanDelay={500}
							onError={handleError}
							onScan={handleScan}
							components={{
								finder: false,
								audio: false,
								torch: false,
							}}
						/>
					)}
					{instruction && <div className="scan-instruction">{instruction}</div>}
				</>
			)}
			<Loading open={loading} variant="compact" spinnerVariant="lines" message={strings.loading} />
			<MessageToast isOpen={Boolean(toastError)} message={toastError} duration={TOAST_DURATION} position="top" />
		</div>
	)

	const renderModal = () => (
		// Re-measure once the modal has finished presenting: the mount-time measure runs while the
		// modal is still animating in, so the container has no size yet and the square overlay would
		// be computed from null dimensions (obscuring the camera).
		<Modal isOpen={isOpen} cssClass="scanner-modal" onDidPresent={() => onResize()} onDidDismiss={() => closeModal()}>
			<ScrollContent>
				<Button variant="clear" onClick={() => closeModal()} className="close-modal-button" aria-label={strings.cancel}>
					<i className="fal fa-xmark" />
				</Button>
				{renderContent()}
			</ScrollContent>
		</Modal>
	)

	if (toggleModal) {
		return renderModal()
	}

	return renderContent()
}

export default ScanEntry
